import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import { NewIcon } from '@/assets/static/icons/todo-page/new';
import { OlderIcon } from '@/assets/static/icons/todo-page/older';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { themeVar } from '@/styles/theme';
import { OPERATIONS_STATUS } from '@trace-8/trace8-schema';
import { AnimatePresence, motion } from 'framer-motion';
import { AssignedToMe, AssignedToMeItem, OwnedByMeItem, OwnedByMeNotAssignedToMe } from '../SubItem';
import { getDateTypeDescription, updateListOfClose } from './utils';

export const statusNextStepRelations = {
  [OPERATIONS_STATUS.READY_FOR_CLIENT_REVIEW]: 'Send to client',
  [OPERATIONS_STATUS.SENT_TO_CLIENT]: 'Waiting for client approval',
  [OPERATIONS_STATUS.CLIENT_APPROVED]: 'Prepare subdoc',
  [OPERATIONS_STATUS.COMMITMENT_SUBMITTED_TO_REPORTING_SYSTEM]: 'Waiting for commitment in reporting system'
};

const allocatorTabsNames = {
  'Investment review': 'Investment Review',
  'Advisor review': 'Advisor Review',
  'Compliance review': 'Compliance Review'
};

const finalCloseImageOptions = {
  toTwoWeeks: '/static/icons/sand-clock.svg',
  fromTwoWeeksToMonth: '/static/icons/calendar-grey.svg',
  fromMonth: '/static/icons/calendar-grey.svg'
};

const finalCloseColorOptions = {
  toTwoWeeks: '#D63B4B',
  fromTwoWeeksToMonth: '#7F7F7F',
  fromMonth: '#7F7F7F'
};

interface Details {
  assignedToMe: AssignedToMe[];
  ownedByMeNotAssignedToMe: OwnedByMeNotAssignedToMe[];
}

interface Summary {
  assignedToMeTaskCount: number;
  newTaskCount: number;
  oldTaskCount: number;
  ownedByMeNotAssignedToMeTaskCount: number;
}

interface Investment {
  finalClose: string;
  id: string;
  name: string;
  summary: Summary;
  nextClose: string;
}

export interface ToDoList {
  investment: Investment;
  details: Details;
}

interface ListProps {
  data: ToDoList;
}

const ToDoList = ({ data }: ListProps) => {
  const navigate = useNavigate();
  const { data: customizationsData } = useCustomizationsData();
  const { isMobile } = useResponsive();
  const { searchParams, setSearchParams, saveScrollPostion, makeScroll } = useSearchParamsManager();

  const openedSection = searchParams.get('openedSection') || '';
  const position = searchParams.get('position') || 0;

  const [investment, setInvestment] = useState({ id: null, name: null });

  const newData = useMemo(() => updateListOfClose(data), [data]);

  useEffect(() => {
    if (openedSection) {
      const { investment } = newData.find((item: any) => item.investment.id === openedSection);
      setInvestment({
        id: investment.id,
        name: investment.name
      });
    }
  }, []);

  useEffect(() => {
    if (data && position) {
      makeScroll(position);
    }
  }, [data]);

  const onRedirect = ({ id, name }: { id?: string; name: string }) => {
    const allocationPage = ['Advisor review', 'Investment review', 'Compliance review'];
    saveScrollPostion();

    if (allocationPage.includes(name)) {
      return navigate(
        `/dealCatalog/allocator/${investment.id}?activeTab=${allocatorTabsNames?.[name as keyof typeof allocatorTabsNames] || 'Begin Allocation'}`
      );
    }

    if (customizationsData?.menuConfig?.transactions) {
      const queryParams = new URLSearchParams({
        ...(id && { filteringBar: JSON.stringify([id]) })
      });
      return navigate(`/transactions?${queryParams.toString()}`);
    }

    if (customizationsData?.menuConfig?.pendingCommitments) {
      return navigate(`/pendingCommitments`);
    }

    // const nextTaskParams = encodeURIComponent(JSON.stringify([statusNextStepRelations[tab as keyof typeof statusNextStepRelations]]));
    // const investmentsParams = encodeURIComponent(JSON.stringify([investment.name]));

    // return navigate(`/recommendations?primaryFilter=${status}&nextTask=${nextTaskParams}&investments=${investmentsParams}`);
  };

  const handleOpenAccordion = (id: string) => {
    setSearchParams(
      (prev) => {
        if (openedSection === id) {
          prev.delete('openedSection');
          return prev;
        }

        prev.set('openedSection', id);
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <AccordionsWrapper>
      {newData?.map((info: any) => (
        <AccordionWrapper key={info.investment.id} $isMobile={isMobile} $isExpanded={openedSection === info.investment.id}>
          <AccordionHeader
            $isMobile={isMobile}
            onClick={() => {
              handleOpenAccordion(info.investment.id);
              setInvestment({
                id: info.investment.id,
                name: info.investment.name
              });
            }}
          >
            <CardHeaderChild $isMobile={isMobile}>
              <CardText>
                <Title $isMobile={isMobile}>{info.investment.name}</Title>
                {!info.shouldHideDateType && info.daysToClose && info.dateObject ? (
                  <SubTitle color={finalCloseColorOptions[info.type as keyof typeof finalCloseColorOptions]}>
                    {<img src={finalCloseImageOptions[info.type as keyof typeof finalCloseColorOptions]} alt={'image'} />}
                    {getDateTypeDescription(info)}
                  </SubTitle>
                ) : (
                  <></>
                )}
              </CardText>
            </CardHeaderChild>
            <AnimatePresence>
              {openedSection !== info.investment.id && (
                <ListInfo
                  $isMobile={isMobile}
                  initial={{ opacity: 0, x: -40, y: 40 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: -40, y: 40 }}
                  transition={{ duration: 0.3 }}
                >
                  <Assigned $isMobile={isMobile}>
                    <Numbers isActive={true}>{info.investment.summary.assignedToMeTaskCount}</Numbers>
                    <Text isActive={true}>Assigned To Me</Text>
                  </Assigned>
                  <Owned $isMobile={isMobile}>
                    <Numbers isActive={false}>{info.investment.summary.ownedByMeNotAssignedToMeTaskCount}</Numbers>
                    <Text isActive={false}>
                      Owned By Me/ <br />
                      Assigned To Someone Else
                    </Text>
                  </Owned>
                  <OpenedIcons $isMobile={isMobile}>
                    <NewIcons>
                      <NewIcon />
                      <IconText color={themeVar('context.success')}>{info.investment.summary.newTaskCount}</IconText>
                    </NewIcons>
                    <OldIcons>
                      <OlderIcon height={22.5} width={22.5} />
                      <IconText color={themeVar('context.warning')}>{info.investment.summary.oldTaskCount}</IconText>
                    </OldIcons>
                  </OpenedIcons>
                </ListInfo>
              )}
            </AnimatePresence>
            <CardSwitcher
              $isMobile={isMobile}
              onClick={() => {
                handleOpenAccordion(info.investment.id);
                setInvestment({
                  id: info.investment.id,
                  name: info.investment.name
                });
              }}
            >
              <ArrowUp
                fill={themeVar('font.action')}
                style={{ position: 'relative', top: '1px', transform: openedSection === info.investment.id ? 'rotate(0deg)' : 'rotate(180deg)' }}
              />
            </CardSwitcher>
          </AccordionHeader>
          <AnimatePresence>
            {openedSection === info.investment.id && (
              <motion.div
                style={{ width: '100%' }}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ListWrapper>
                  <FirstListWrapper>
                    <ListTitle>
                      <Numbers isActive={true}>{info.investment.summary.assignedToMeTaskCount}</Numbers>
                      <Text isActive={true}>Assigned To Me</Text>
                    </ListTitle>
                    <FirstSubListWrap $isMobile={isMobile}>
                      <AssignedToMeItem data={info.details} onRedirect={onRedirect} />
                    </FirstSubListWrap>
                  </FirstListWrapper>
                  <SecondListWrapper>
                    <OpenedListTitle>
                      <Numbers isActive={false}>{info.investment.summary.ownedByMeNotAssignedToMeTaskCount}</Numbers>
                      <Text isActive={false}>Owned By Me/Assigned To Someone Else</Text>
                    </OpenedListTitle>
                    <SubListWrap $isMobile={isMobile}>
                      <OwnedByMeItem data={info.details} onRedirect={onRedirect} />
                    </SubListWrap>
                  </SecondListWrapper>
                </ListWrapper>
                {isMobile ? (
                  <CardSwitcher
                    $isMobile={isMobile}
                    onClick={() => {
                      handleOpenAccordion(info.investment.id);
                      setInvestment({
                        id: info.investment.id,
                        name: info.investment.name
                      });
                    }}
                  >
                    <Switcher>
                      <ArrowUp fill={themeVar('font.action')} width={24} height={24} />
                    </Switcher>
                  </CardSwitcher>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </AccordionWrapper>
      ))}
    </AccordionsWrapper>
  );
};

export default ToDoList;

const AccordionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 0;
`;

const SubListWrap = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: center;
  gap: 20px;
  margin-left: 10px;
  margin-top: 4px;
`;
const FirstSubListWrap = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: center;
  gap: 20px;
  margin-left: 10px;
  margin-top: 20px;
`;

export const Line = styled.div<{ $isMobile: boolean }>`
  display: flex;
  width: 20px;
  height: 1px;
  background: ${({ theme }) => theme.border.base};
  transform: ${({ $isMobile }) => ($isMobile ? 'rotate(270deg)' : 'rotate(-90deg)')};
  &:last-child {
    display: none;
  }
`;
const ListInfo = styled(motion.div)<{ $isMobile: boolean }>`
  display: flex;
  margin: ${({ $isMobile }) => ($isMobile ? '0 auto' : '0')};
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  width: ${({ $isMobile }) => ($isMobile ? 'auto' : '45%')};
  gap: ${({ $isMobile }) => ($isMobile ? '20px' : '0')};
  justify-content: space-between;
`;
const NewIcons = styled.div`
  display: flex;
  gap: 8px;
`;
const OldIcons = styled.div`
  display: flex;
  gap: 8px;
`;
const OpenedIcons = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 25px;
  width: ${({ $isMobile }) => ($isMobile ? 'auto' : '20%')};
`;
const Owned = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: ${({ $isMobile }) => ($isMobile ? 'auto' : '30%')};
`;
const Assigned = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: ${({ $isMobile }) => ($isMobile ? 'auto' : '30%')};
`;
const CardHeaderChild = styled.div<{ $isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  width: ${({ $isMobile }) => ($isMobile ? 'auto' : '50%')};
`;
const IconText = styled.div<{ color?: string }>`
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 22px;
  text-align: center;
  color: ${({ color }) => color};
`;
const CardSwitcher = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '50px')};
  height: ${({ $isMobile }) => ($isMobile ? '0' : '50px')};
  min-width: 50px;
  border-radius: 42px;
  margin: ${({ $isMobile }) => ($isMobile ? '10px 0' : '0 26px 0 0')};
  margin-left: auto;
  user-select: none;

  &:hover {
    svg {
      path {
        fill: ${({ theme }) => theme.context.light};
      }
    }

    background: ${({ theme }) => theme.action.hover};
  }
`;
const AccordionHeader = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  display: flex;
  align-items: ${({ $isMobile }) => ($isMobile ? 'flex-start' : 'center')};
  padding: ${({ $isMobile }) => ($isMobile ? '0px 15px' : '0')};
  gap: ${({ $isMobile }) => ($isMobile ? '35px' : '20px')};
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  cursor: pointer;
`;
const CardText = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;
const Switcher = styled.div`
  display: flex;
  align-items: center;
`;
const Text = styled.div<{ isActive?: boolean }>`
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: ${({ isActive }) => (isActive ? 700 : 400)};
  font-size: ${({ isActive }) => (isActive ? '19px' : '16px')};
  line-height: ${({ isActive }) => (isActive ? '20px' : '16px')};
  color: ${({ isActive, theme }) => (isActive ? theme.font.base : theme.font.weak)};
`;

const Numbers = styled.div<{ isActive?: boolean }>`
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: ${({ isActive }) => (isActive ? 700 : 600)};
  font-size: ${({ isActive }) => (isActive ? '50px' : '33px')};
  line-height: ${({ isActive }) => (isActive ? '50px' : '30px')};
  color: ${({ isActive, theme }) => (isActive ? theme.font.base : theme.font.weak)};
`;
const SecondListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
  padding: 35px 30px;
  border: 1px solid ${({ theme }) => theme.border.base};
  border-radius: 0 0 10px 10px;
`;
const FirstListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.layer[1]};
  padding: 35px 30px;
  border: 1px solid ${({ theme }) => theme.border.base};
  border-bottom: none;
  border-radius: 10px 10px 0px 0px;
`;
const ListTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;
const OpenedListTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ListWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  margin-top: 20px;
`;
const AccordionWrapper = styled.div<{ $isMobile: boolean; $isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ $isMobile }) => ($isMobile ? '10px 15px' : '30px 40px')};
  gap: 10px;
  background: ${({ theme }) => theme.layer.base};
  border: 1px solid ${({ theme }) => theme.border.base};
  border-radius: 10px;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    css`
      &:hover {
        background: ${({ theme }) => theme.layer[2]};

        ${CardSwitcher} {
          svg {
            path {
              fill: ${({ theme }) => theme.context.light};
            }
          }
        }

        &:hover ${CardSwitcher} {
          background: ${({ theme }) => theme.action.hover};
        }
      }
    `}
`;
const Title = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 600;
  font-size: 23px;
  line-height: ${({ $isMobile }) => ($isMobile ? '28px' : '42px')};
  color: ${({ theme }) => theme.font.base};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '10px' : '0')};
`;
const SubTitle = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.7px;
  color: ${({ color }) => color};
  padding-left: 5px;
  gap: 5px;
`;
